export enum UserRole {
    CLIENT = 'CLIENT',
    MODERATOR = 'MODERATOR',
    SIGNATORY = 'SIGNATORY',
    SUPER_ADMIN = 'SUPER_ADMIN',
    EMPLOYEE = 'EMPLOYEE'
}

export enum UserStatus {
    DRAFT = 'DRAFT',
    PENDING_MODERATION = 'PENDING_MODERATION',
    NEEDS_REVISION = 'NEEDS_REVISION',
    APPROVED = 'APPROVED',
    ACTIVE = 'ACTIVE',
    VERIFIED = 'VERIFIED',
    REJECTED = 'REJECTED',
    BLOCKED = 'BLOCKED',
    DELETED = 'DELETED'
}

export interface User {
    id: number;
    login: string;
    fullName: string;
    role: UserRole;
    status: UserStatus;
    companyId?: number;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
    rejectionReason?: string;
    moderatorComments?: string;
    phoneNumber?: string;
    email?: string;
    iin?: string;
    position?: string;
    from1C?: boolean;
}

export interface AuthResponse {
    accessToken: string;
    refreshToken: string;
    tokenType: string;
    userId: number;
    login: string;
    fullName: string;
    role: UserRole;
    status: UserStatus;
    companyId?: number;
    from1C?: boolean;
}

export interface LoginRequest {
    login: string;
    password: string;
}

export interface RegisterRequest {
    login: string;
    password: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    phoneNumber?: string;
    email?: string;
    iin?: string;
    position?: string;
    companyBin?: string;
}

export interface OtpVerificationRequest {
    login: string;
    otpCode: string;
}

export interface Company {
    id?: number;
    bin: string;
    name: string;
    legalAddress: string;
    actualAddress: string;
    contactPhone: string;
    contactEmail: string;
    directorName: string;
    // Банковские реквизиты (из базы данных)
    bankName?: string;
    bankBik?: string;
    bankAccount?: string;
    bankKbe?: string;
    // Старые поля банковских реквизитов (для обратной совместимости)
    bank?: string;
    kbe?: string;
    bik?: string;
    accountNumber?: string;
    factAddress?: string;
    directorIin?: string;
    phoneNumber?: string;
    email?: string;
    // Флаг компании из 1С
    from1C?: boolean;
    status?: UserStatus;
    rejectionReason?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface CompanyRegistrationRequest {
    bin: string;
    name: string;
    legalAddress: string;
    actualAddress: string;
    contactPhone: string;
    contactEmail: string;
    directorName: string;
    bank?: string;
    kbe?: string;
    bik?: string;
    accountNumber?: string;
    factAddress?: string;
    directorIin?: string;
    phoneNumber?: string;
    email?: string;
}

export interface ApiResponse<T = any> {
    success: boolean;
    message: string;
    data?: T;
    types?: Record<string, string>;
    documents?: any[];
    url?: string;
}

// PKB API Types
export interface PKBTokenResponse {
    access: {
        hash: string;
        expires_at: string;
        ttl: number;
        issue_date: string;
    };
    refresh: {
        hash: string;
        expires_at: string;
        ttl: number;
        issue_date: string;
    };
    pass_change_needed: boolean;
}

export interface PKBOrganization {
    bin: string;
    name_ru: string;
    name_kk?: string;
    name_en?: string;
    reg_status: {
        code: string;
        ru: string;
        kk?: string;
    };
    organization_leader?: {
        iin: string;
        position?: string;
        appointment_date?: string;
        position_type?: string;
    };
    founders_ul?: Array<{ bin: string }>;
    founders_fl?: Array<{ iin: string }>;
    address: {
        district_ru?: string;
        region_ru?: string;
        city_ru?: string;
        street_ru?: string;
        building_number?: string;
        apartment_number?: string;
        postal_code?: string;
        classifier_codes?: {
            region_code?: string;
            district_code?: string;
        };
        premises_type?: string;
    };
    activity: {
        main: boolean;
        oked: string;
        activity_ru: string;
        activity_kk?: string;
    }[];
    registration_date?: string;
    last_change_date?: string;
    legal_form?: {
        code: string;
        ru: string;
        kk?: string;
    };
    entrepreneurship_type?: {
        code: string;
        ru: string;
        kk?: string;
    };
    beneficiaries?: Array<{
        iin?: string;
        bin?: string;
        share_percentage?: number;
    }>;
}

export interface PKBCompanyResponse {
    organization: PKBOrganization;
}

export interface PKBErrorResponse {
    error: string;
    message: string;
    code?: string;
}

export interface PKBCompanyData {
    bin: string;
    name: string;
    legalAddress: string;
    actualAddress: string;
    contactPhone: string;
    contactEmail: string;
    directorName: string;
    bank?: string;
    kbe?: string;
    bik?: string;
    accountNumber?: string;
    pkbData: {
        status: PKBOrganization['reg_status'];
        registrationDate?: string;
        lastChangeDate?: string;
        legalForm?: PKBOrganization['legal_form'];
        entrepreneurshipType?: PKBOrganization['entrepreneurship_type'];
        founders: {
            legal: Array<{ bin: string }>;
            individual: Array<{ iin: string }>;
        };
        activities: PKBOrganization['activity'];
        beneficiaries: PKBOrganization['beneficiaries'];
    };
}

// Profile Management Types
export interface PersonalDataUpdateRequest {
    firstName: string;
    lastName: string;
    middleName?: string;
    phoneNumber?: string;
    email?: string;
    iin?: string;
}

export interface CompanyBankingDetails {
    id?: number;
    companyId: number;
    bankName?: string;
    kbe?: string;
    bik?: string;
    accountNumber?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface CompanyInfoUpdateRequest {
    name: string;
    bin?: string;
    legalAddress: string;
    factAddress?: string;
    contactPhone: string;
    contactEmail: string;
    directorName: string;
    directorIin?: string;
    bankName?: string;
    kbe?: string;
    bik?: string;
    accountNumber?: string;
}

export interface CompanyEmployee {
    id?: number;
    companyId: number;
    firstName: string;
    lastName: string;
    middleName?: string;
    iin?: string;
    position: string;
    phoneNumber?: string;
    email?: string;
    isActive: boolean;
    hireDate?: string;
    terminationDate?: string;
    createdAt?: string;
    updatedAt?: string;
    fullName?: string;
}

export interface EmployeeCreateRequest {
    firstName: string;
    lastName: string;
    position: string;
    phoneNumber?: string;
    email?: string;
    isActive?: boolean;
}

export interface EmployeeUpdateRequest {
    firstName: string;
    lastName: string;
    position: string;
    phoneNumber?: string;
    email?: string;
    isActive?: boolean;
}

export interface ProfileData {
    user: User;
    company?: Company;
    bankingDetails?: CompanyBankingDetails;
    employees?: CompanyEmployee[];
}

export interface EmployeeInviteRequest {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    email?: string;
    position: string;
    isSignatory: boolean;
}
